import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { formatMoney } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { Calendar } from "../../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { Plus, CalendarDays, Trash2, Send, Clock } from "lucide-react";
import ReservationTimeline from "./ReservationTimeline";
import { getUser } from "../../lib/auth";

const STATUS_COLOR = {
  hold: "bg-[#F7F4EF] text-[#22302E] border-[#E4E4E7]",
  pending: "bg-[#3B9FD4]/10 text-[#8B6F2F] border-[#3B9FD4]/40",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  confirmed: "bg-[#0AA89C]/10 text-[#0AA89C] border-[#0AA89C]/30",
  cancelled: "bg-[#F7F4EF] text-[#6B7280] border-[#E4E4E7]",
  refunded: "bg-blue-50 text-blue-800 border-blue-200",
};

const CHANNEL_LABEL = {
  direct: "Site próprio",
  airbnb: "Airbnb",
  booking: "Booking.com",
  other: "Outro",
};

function isoDate(d) {
  return format(d, "yyyy-MM-dd");
}

export default function AdminReservations() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");
  const [creating, setCreating] = useState(false);

  async function load() {
    const r = await api.get("/admin/reservations");
    setRows(r.data);
  }
  useEffect(() => { load(); }, []);

  async function confirmRow(id) {
    if (!window.confirm("Confirmar esta reserva definitivamente?")) return;
    try {
      await api.post(`/admin/reservations/${id}/confirm`);
      toast.success("Reserva confirmada");
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  }

  async function cancelRow(id) {
    const reason = window.prompt("Motivo do cancelamento (opcional)");
    if (reason === null) return;
    try {
      await api.post(`/admin/reservations/${id}/cancel`, { reason });
      toast.success("Reserva cancelada");
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  }

  async function deleteRow(id) {
    if (!window.confirm("Mover para a Lixeira? A reserva ficará oculta por 30 dias antes da exclusão automática.")) return;
    const reason = window.prompt("Motivo (opcional):") || "";
    try {
      await api.delete(`/admin/reservations/${id}`, { data: { reason } });
      toast.success("Movida para Lixeira");
      setSelected(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao excluir"); }
  }

  async function releaseCheckin(id) {
    if (!window.confirm("Liberar check-in ao hóspede? Um link será gerado e enviado por e-mail.")) return;
    try {
      const r = await api.post(`/admin/reservations/${id}/release-checkin`);
      if (r.data.email_sent) toast.success(`Check-in liberado · e-mail enviado (${r.data.message_id?.slice(0,8)}…)`);
      else toast.warning(`Link gerado, mas e-mail falhou: ${r.data.email_error || "?"} — use o WhatsApp.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao liberar check-in"); }
  }

  const filtered = filter ? rows.filter((r) => r.status === filter) : rows;

  return (
    <div data-testid="admin-reservations-page">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="text-[11px] uppercase tracking-[0.28em] text-[#6B7280]">PMS</span>
          <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Reservas</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex gap-1 text-xs">
            {[
              ["", "Todas"],
              ["hold", "Hold"],
              ["pending", "Pendentes"],
              ["paid", "Pagas"],
              ["confirmed", "Confirmadas"],
              ["cancelled", "Canceladas"],
            ].map(([v, l]) => (
              <button
                key={v}
                data-testid={`filter-${v || "all"}`}
                onClick={() => setFilter(v)}
                className={`px-3 py-1.5 rounded border ${filter === v ? "border-[#0AA89C] text-[#0AA89C] bg-white" : "border-[#E4E4E7] text-[#6B7280] bg-white"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <Button onClick={() => setCreating(true)} className="bg-[#0AA89C] hover:bg-[#066B63]" data-testid="new-manual-reservation-btn">
            <Plus className="w-4 h-4 mr-1" /> Reserva manual
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F4EF] border-b border-[#E4E4E7]">
            <tr className="text-left">
              <Th>Código</Th>
              <Th>Casa</Th>
              <Th>Hóspede</Th>
              <Th>Canal</Th>
              <Th>Check-in</Th>
              <Th>Check-out</Th>
              <Th>Total</Th>
              <Th>Status</Th>
              <Th className="text-right">Ações</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F7F4EF]">
            {filtered.map((r) => (
              <tr key={r.id} className="hover:bg-[#F7F4EF]" data-testid={`reservation-row-${r.code}`}>
                <Td className="font-mono text-xs">{r.code}</Td>
                <Td>{r.house_name}</Td>
                <Td>{r.guest.name}</Td>
                <Td className="text-xs text-[#6B7280]">{CHANNEL_LABEL[r.channel] || "Site próprio"}</Td>
                <Td>{r.check_in}</Td>
                <Td>{r.check_out}</Td>
                <Td>{formatMoney(r.total)}</Td>
                <Td>
                  <span className={`inline-block text-xs px-2 py-0.5 border rounded ${STATUS_COLOR[r.status]}`}>
                    {r.status}
                  </span>
                </Td>
                <Td className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setSelected(r.id)} data-testid={`view-${r.code}`}>
                    Ver
                  </Button>
                </Td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="text-center py-12 text-[#6B7280]">Nenhuma reserva registrada.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ReservationDetail id={selected} onClose={() => setSelected(null)} onConfirm={confirmRow} onCancel={cancelRow} onDelete={deleteRow} onReleaseCheckin={releaseCheckin} />
      <ManualReservationDialog open={creating} onClose={() => { setCreating(false); load(); }} />
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-4 py-3 text-[#22302E] ${className}`}>{children}</td>;
}

function ReservationDetail({ id, onClose, onConfirm, onCancel, onDelete, onReleaseCheckin }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const user = getUser();
  const canDelete = ["diretor", "gestor"].includes(user?.role);
  useEffect(() => {
    if (id) api.get(`/admin/reservations/${id}`).then((r) => setData(r.data));
    else { setData(null); setTab("overview"); }
  }, [id]);

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl bg-white max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reserva {data?.reservation?.code || ""}</DialogTitle>
          <DialogDescription>Centro operacional da reserva — visão geral, timeline e ações.</DialogDescription>
        </DialogHeader>
        {data && (
          <>
            <div className="flex gap-2 flex-wrap mb-4 border-b border-slate-200 pb-2">
              {[["overview", "Visão geral"], ["timeline", "Timeline"], ["payments", "Pagamentos"]].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  className={`text-xs px-3 py-1.5 rounded ${tab === k ? "bg-[#0AA89C] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`rsv-tab-${k}`}
                >
                  {l}
                </button>
              ))}
            </div>
            {tab === "overview" && (
              <>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <Info label="Casa" value={data.house?.name} />
                  <Info label="Status" value={data.reservation.status} />
                  <Info label="Canal" value={CHANNEL_LABEL[data.reservation.channel] || "Site próprio"} />
                  <Info label="Referência externa" value={data.reservation.external_ref || "—"} />
                  <Info label="Check-in" value={data.reservation.check_in} />
                  <Info label="Check-out" value={data.reservation.check_out} />
                  <Info label="Hóspedes" value={data.reservation.guests} />
                  <Info label="Noites" value={data.reservation.nights} />
                  <Info label="Titular" value={data.reservation.guest.name} />
                  <Info label="E-mail" value={data.reservation.guest.email} />
                  <Info label="Telefone" value={data.reservation.guest.phone} />
                  <Info label="Total" value={formatMoney(data.reservation.total)} />
                </div>
                {data.reservation.notes && (
                  <div className="mt-4 text-sm">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-[#6B7280] mb-1">Observações</div>
                    <div className="text-[#22302E] whitespace-pre-line">{data.reservation.notes}</div>
                  </div>
                )}
              </>
            )}
            {tab === "timeline" && <ReservationTimeline reservationId={id} />}
            {tab === "payments" && (
              <>
                {data.payments.length === 0 && <div className="text-sm text-slate-500 p-4">Nenhum pagamento associado.</div>}
                {data.payments.map((p) => (
                  <div key={p.id} className="border border-slate-200 rounded-md p-3 mb-2 text-sm">
                    <div className="flex justify-between font-medium">
                      <span>{p.provider} — {p.method || p.payment_method_id || "—"}</span>
                      <span className={p.status === "approved" ? "text-emerald-600" : "text-slate-500"}>{p.status} · {formatMoney(p.amount)}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-2 grid grid-cols-2 gap-1">
                      <div>Interno: <b>{p.internal_payment_status || "—"}</b></div>
                      <div>MP status: <b>{p.mp_status || "—"}</b></div>
                      <div>Preference: <code>{p.mp_preference_id?.slice(0, 20) || "—"}</code></div>
                      <div>Payment ID: <code>{p.mp_payment_id || "—"}</code></div>
                      <div>live_mode: <b>{String(p.live_mode ?? "—")}</b></div>
                      <div>env: <b>{p.environment || "—"}</b> · <b>{p.credential_kind || "—"}</b></div>
                      <div>Webhook: {p.webhook_received_at?.slice(0, 19).replace("T", " ") || "—"}</div>
                      <div>Sync: {p.last_sync_at?.slice(0, 19).replace("T", " ") || "—"}</div>
                    </div>
                  </div>
                ))}
              </>
            )}
            <div className="mt-6 flex justify-between gap-2 flex-wrap">
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" onClick={() => onCancel(id)} data-testid="cancel-reservation">Cancelar reserva</Button>
                {canDelete && (
                  <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => onDelete(id)} data-testid="rsv-delete-btn">
                    <Trash2 className="w-4 h-4 mr-1" /> Mover p/ Lixeira
                  </Button>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                {["paid", "confirmed"].includes(data.reservation.status) && (
                  <Button variant="outline" className="border-[#3B9FD4] text-[#3B9FD4] hover:bg-[#3B9FD4]/10" onClick={() => onReleaseCheckin(id)} data-testid="rsv-release-checkin">
                    <Send className="w-4 h-4 mr-1" /> Liberar check-in
                  </Button>
                )}
                {data.reservation.status !== "confirmed" && data.reservation.status !== "cancelled" && (
                  <Button className="bg-[#0AA89C] hover:bg-[#066B63] text-white" onClick={() => onConfirm(id)} data-testid="confirm-reservation">
                    Confirmar definitivamente
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#6B7280]">{label}</div>
      <div className="text-[#22302E]">{value ?? "—"}</div>
    </div>
  );
}

function ManualReservationDialog({ open, onClose }) {
  const [houses, setHouses] = useState([]);
  const [form, setForm] = useState({
    house_id: "",
    channel: "airbnb",
    external_ref: "",
    range: { from: undefined, to: undefined },
    guests: 2,
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    total: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) api.get("/admin/houses").then((r) => setHouses(r.data));
    if (!open) setForm({
      house_id: "", channel: "airbnb", external_ref: "",
      range: { from: undefined, to: undefined }, guests: 2,
      guest_name: "", guest_email: "", guest_phone: "", total: "", notes: "",
    });
  }, [open]);

  async function submit() {
    if (!form.house_id) { toast.error("Selecione a casa"); return; }
    if (!form.range.from || !form.range.to) { toast.error("Informe check-in e check-out"); return; }
    if (!form.guest_name) { toast.error("Nome do hóspede é obrigatório"); return; }
    setSaving(true);
    try {
      const body = {
        house_id: form.house_id,
        channel: form.channel,
        external_ref: form.external_ref,
        check_in: isoDate(form.range.from),
        check_out: isoDate(form.range.to),
        guests: Number(form.guests) || 1,
        guest: {
          name: form.guest_name,
          email: form.guest_email || "sem-email@azuhomes.local",
          phone: form.guest_phone,
        },
        notes: form.notes,
      };
      if (form.total) body.total = Number(form.total);
      await api.post("/admin/reservations/manual", body);
      toast.success("Reserva registrada");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registrar");
    } finally {
      setSaving(false);
    }
  }

  const label = form.range.from
    ? form.range.to
      ? `${format(form.range.from, "d MMM", { locale: ptBR })} — ${format(form.range.to, "d MMM", { locale: ptBR })}`
      : `${format(form.range.from, "d MMM", { locale: ptBR })} — ?`
    : "Selecione o período";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader>
          <DialogTitle>Registrar reserva de canal externo</DialogTitle>
          <DialogDescription>
            Utilize este formulário para lançar no calendário central reservas confirmadas em plataformas
            externas (Airbnb, Booking) ou recebidas por telefone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Casa</Label>
            <Select value={form.house_id} onValueChange={(v) => setForm({ ...form, house_id: v })}>
              <SelectTrigger data-testid="manual-house-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent className="bg-white">
                {houses.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Canal</Label>
              <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                <SelectTrigger data-testid="manual-channel"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="airbnb">Airbnb</SelectItem>
                  <SelectItem value="booking">Booking.com</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Referência externa</Label>
              <Input value={form.external_ref} onChange={(e) => setForm({ ...form, external_ref: e.target.value })} placeholder="Ex: HMXXX" data-testid="manual-external-ref" />
            </div>
          </div>
          <div>
            <Label>Período da estadia</Label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="manual-date-trigger"
                  className="w-full text-left px-3 py-2 border border-[#E4E4E7] rounded-md flex items-center gap-3 text-sm hover:border-[#0AA89C]"
                >
                  <CalendarDays className="w-4 h-4 text-[#0AA89C]" />
                  {label}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="range"
                  locale={ptBR}
                  numberOfMonths={2}
                  selected={form.range}
                  onSelect={(v) => setForm({ ...form, range: v || { from: undefined, to: undefined } })}
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Hóspedes</Label>
              <Input type="number" min={1} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} data-testid="manual-guests" />
            </div>
            <div>
              <Label>Total pago (R$)</Label>
              <Input type="number" step="0.01" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} placeholder="Opcional — usa preço padrão se vazio" data-testid="manual-total" />
            </div>
          </div>
          <div>
            <Label>Nome do hóspede</Label>
            <Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} data-testid="manual-guest-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>E-mail (opcional)</Label>
              <Input type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} data-testid="manual-guest-email" />
            </div>
            <div>
              <Label>Telefone (opcional)</Label>
              <Input value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} data-testid="manual-guest-phone" />
            </div>
          </div>
          <div>
            <Label>Observações</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
          </div>
          <Button className="w-full bg-[#0AA89C] hover:bg-[#066B63]" onClick={submit} disabled={saving} data-testid="manual-submit">
            {saving ? "Registrando..." : "Registrar reserva"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
